/**
 * One account: who they are, what they may do, and the things you do to an
 * account — reset the password, sign them out, deactivate, delete.
 *
 * All of it in place with a back link, no modals. The destructive half is
 * gathered at the bottom under its own heading rather than sitting next to
 * "Save", so nothing irreversible is one mis-click from something routine.
 */
import { useState } from "react";
import { completeGrants, type Grants } from "@dp/shared";
import { ApiError, api } from "../../lib/api.ts";
import { useRouter } from "../../lib/router.tsx";
import { useAsync } from "../../lib/useAsync.ts";
import { useAuth } from "../../lib/auth.tsx";
import { BackLink, Loading, PageHead, Problem } from "../../components/Page.tsx";
import { PermissionMatrix } from "./PermissionMatrix.tsx";
import type { AccountRow } from "./Accounts.tsx";

export function AccountEditor({ id }: { id: string }) {
  const detail = useAsync(() => api.get<{ account: AccountRow }>(`/api/accounts/${id}`), [id]);
  if (detail.loading && !detail.data) return <Loading what="the account" />;
  if (detail.error) return <Problem error={detail.error} />;
  if (!detail.data) return null;
  return <Editor key={id} account={detail.data.account} onReload={detail.reload} />;
}

function Editor({ account, onReload }: { account: AccountRow; onReload: () => void }) {
  const { can, user, presets, refresh } = useAuth();
  const { navigate } = useRouter();

  const [name, setName] = useState(account.name);
  const [email, setEmail] = useState(account.email);
  const [isAdmin, setIsAdmin] = useState(account.isAdmin);
  const [grants, setGrants] = useState<Grants>(() => completeGrants(account.grants));
  const [newPassword, setNewPassword] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const mayEdit = can("accounts", "edit");
  const mayDelete = can("accounts", "delete");
  const isMe = account.id === user?.id;

  async function run(what: string, work: () => Promise<unknown>) {
    setProblem(null);
    setDone(null);
    setBusy(true);
    try {
      await work();
      setDone(what);
      onReload();
      // Changing your own account changes what you may do next.
      if (isMe) await refresh();
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : `Could not ${what.toLowerCase()}.`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <BackLink to="/accounts">Accounts</BackLink>
      <PageHead
        title={account.name}
        sub={<>
          {account.email}
          {isMe && <span className="chip" style={{ marginLeft: 8 }}>you</span>}
          {!account.active && <span className="chip chip-bad" style={{ marginLeft: 8 }}>Deactivated</span>}
          {account.activeSessions > 0 && <span className="chip chip-ok" style={{ marginLeft: 8 }}>
            signed in on {account.activeSessions} {account.activeSessions === 1 ? "device" : "devices"}
          </span>}
        </>}
      />

      {problem && <div className="notice notice-bad">{problem}</div>}
      {done && <div className="notice notice-ok">{done}.</div>}

      <div className="card">
        <div className="card-head"><h2>Identity</h2></div>
        <div className="card-body stack">
          <div className="field-row">
            <div className="field">
              <label htmlFor="edit-name">Name</label>
              <input id="edit-name" className="control" value={name} disabled={!mayEdit}
                onChange={(event) => setName(event.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="edit-email">Email address</label>
              <input id="edit-email" className="control" type="email" value={email} disabled={!mayEdit}
                onChange={(event) => setEmail(event.target.value)} />
            </div>
          </div>

          <label className="checkline">
            <input type="checkbox" checked={isAdmin} disabled={!mayEdit}
              onChange={(event) => setIsAdmin(event.target.checked)} />
            <span>
              Administrator
              <div className="small muted">
                Can do everything, including managing accounts. Turning this on or off signs them out.
              </div>
            </span>
          </label>
        </div>
        {mayEdit && (
          <div className="card-foot">
            <button className="btn btn-primary" type="button" disabled={busy}
              onClick={() => void run("Saved", () => api.patch(`/api/accounts/${account.id}`, { name, email, isAdmin }))}>
              Save identity
            </button>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-head"><h2>What they may do</h2></div>
        <div className="card-body">
          <PermissionMatrix
            grants={grants} disabled={!mayEdit} isAdmin={isAdmin} presets={presets} onChange={setGrants}
          />
        </div>
        {mayEdit && (
          <div className="card-foot">
            <span className="small muted spacer">Takes effect on their next click — nobody is signed out.</span>
            <button className="btn btn-primary" type="button" disabled={busy}
              onClick={() => void run("Permissions saved", () => api.put(`/api/accounts/${account.id}/grants`, { grants }))}>
              Save permissions
            </button>
          </div>
        )}
      </div>

      {mayEdit && (
        <div className="card">
          <div className="card-head"><h2>Password</h2></div>
          <div className="card-body stack">
            <div className="field">
              <label htmlFor="reset-password">Set a new password</label>
              <input id="reset-password" className="control" type="text" value={newPassword} minLength={12}
                placeholder="At least 12 characters"
                onChange={(event) => setNewPassword(event.target.value)} />
              <span className="hint">
                Resetting signs them out everywhere. They can change it themselves from their own account page.
              </span>
            </div>
          </div>
          <div className="card-foot">
            <button className="btn" type="button" disabled={busy}
              onClick={() => void run("Signed out everywhere", () => api.post(`/api/accounts/${account.id}/sign-out`))}>
              Sign them out everywhere
            </button>
            <button className="btn btn-primary" type="button" disabled={busy || newPassword.length < 12}
              onClick={() => void run("Password reset", async () => {
                await api.post(`/api/accounts/${account.id}/password`, { newPassword });
                setNewPassword("");
              })}>
              Reset password
            </button>
          </div>
        </div>
      )}

      {mayEdit && (
        <div className="card danger">
          <div className="card-head"><h2>Closing this account</h2></div>
          <div className="card-body stack-sm">
            <p className="small muted">
              Deactivating keeps everything they did and stops them signing in; it can be undone. Deleting removes the
              account and its permissions, and cannot.
            </p>
          </div>
          <div className="card-foot">
            <button className="btn" type="button" disabled={busy}
              onClick={() => void run(account.active ? "Deactivated" : "Reactivated",
                () => api.patch(`/api/accounts/${account.id}`, { active: !account.active }))}>
              {account.active ? "Deactivate" : "Reactivate"}
            </button>
            {mayDelete && !isMe && (
              <button className="btn btn-danger" type="button" disabled={busy}
                onClick={() => void run("Deleted", async () => {
                  await api.del(`/api/accounts/${account.id}`);
                  navigate("/accounts");
                })}>
                Delete permanently
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
